import { CreateStepper, useCreateStepper } from '@/lib/Steppers';
import { FC, useEffect } from 'react';
import { CREATE_DATA_SOURCE_CRM, CREATE_DATA_SOURCE_CRM_STEPS } from './constants';
import { Loadable } from '@/components/loaders';
import { CheckInstallationStep, CrmDataSourceFieldsStep, CrmDataSourceParametersStep, CrmDataSourceSourceDetailsStep, InstallationSuccessStep } from './steps';
import { RouteConstants } from '@/features/router/RouteConstants';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useGetDataSourceTypesQuery } from '@/services/dataSources';
import { DataSource, DataSourceCrmConfig, DataSourceIDEnum } from '@/models/dataSource';
import { clearStashedDataSource } from '@/features/dataSources/dataSourcesSlice';
import { useAppSelector } from '@/reducers';

type CreateDataSourceCrmContentProps = {
  dataSourceId: DataSourceIDEnum;
  modalProps?: {
    onSuccess: (dataSource: DataSource) => void;
    onSuccessAndContinue?: (dataSource: DataSource) => void;
    closeModal: VoidFunction;
  };
};

export const CreateDataSourceCrmContent: FC<CreateDataSourceCrmContentProps> = ({ dataSourceId, modalProps }) => {
  const dispatch = useDispatch();
  const { steps, currentStepId, currentStepIndex, goToNextStep, goToPreviousStep, completeCurrentStep, goToStep } =
    useCreateStepper(CREATE_DATA_SOURCE_CRM_STEPS);

  const history = useHistory();

  const { isFetching, data: sources } = useGetDataSourceTypesQuery();
  const dataSourceType = sources?.find((source) => source.id === dataSourceId);
  const dataSource = useAppSelector((state) => state.dataSources.edit?.dataSource) as Partial<DataSource<DataSourceCrmConfig>> | undefined;

  useEffect(() => {
    dispatch(clearStashedDataSource());
  }, []);

  const handleNextStep = () => {
    completeCurrentStep();
    goToNextStep();
  };

  const handlePrevStep = () => {
    goToPreviousStep();
  };

  const handleCancel = () => {
    if (modalProps) {
      modalProps.closeModal();
    } else {
      history.push(`${RouteConstants.dataSources}?catalog`);
    }
  };

  const handleCloseAndContinue = () => {
    if (modalProps && dataSource) {
      if (modalProps.onSuccessAndContinue) {
        modalProps.onSuccessAndContinue(dataSource as DataSource);
      } else {
        modalProps.onSuccess(dataSource as DataSource);
      }
    } else {
      history.push(RouteConstants.audiencesCreate);
    }
  };

  const handleCloseOnly = () => {
    if (modalProps && dataSource) {
      modalProps.onSuccess(dataSource as DataSource);
    } else {
      history.push(RouteConstants.dataSources);
    }
  };

  const stepTemplate = () => {
    switch (currentStepId) {
      case CREATE_DATA_SOURCE_CRM.SOURCE_DETAILS:
        return (
          <CrmDataSourceSourceDetailsStep
            onNextStep={handleNextStep}
            onPrevStep={handlePrevStep}
            onGoToStep={goToStep}
            onCancel={handleCancel}
            dataSourceType={dataSourceType}
          />
        );

      case CREATE_DATA_SOURCE_CRM.UPDATE_PARAMETERS:
        return <CrmDataSourceParametersStep onNextStep={handleNextStep} onPrevStep={handlePrevStep} onGoToStep={goToStep} onCancel={handleCancel} />;

      case CREATE_DATA_SOURCE_CRM.UPDATE_FIELDS:
        return <CrmDataSourceFieldsStep onNextStep={handleNextStep} onPrevStep={handlePrevStep} onGoToStep={goToStep} onCancel={handleCancel} />;

      case CREATE_DATA_SOURCE_CRM.CHECK_INSTALLATION:
        return <CheckInstallationStep onNextStep={handleNextStep} onPrevStep={handlePrevStep} onGoToStep={goToStep} onCancel={handleCancel} />;

      case CREATE_DATA_SOURCE_CRM.INSTALLATION_SUCCESS:
        return <InstallationSuccessStep onInit={completeCurrentStep} onNextStep={handleCloseAndContinue} onPrevStep={handleCloseOnly} isModal={!!modalProps} />;

      default:
        return null;
    }
  };

  return (
    <>
      <CreateStepper steps={steps} currentStepIndex={currentStepIndex} />

      <Loadable isLoading={isFetching}>{stepTemplate()}</Loadable>
    </>
  );
};
